import * as Sentry from "@sentry/react";
import { ApiTrack, ErrorMeta, attachErrorMeta, canAttachErrorMeta, hasBeenCaptured, markCaptured, readErrorMetaChain } from "./meta";
import { matchApiPath, matchPageRoute } from "./path";

/**
 * 에러 객체에 수집한 데이터를 Sentry 이벤트로 옮기고, 캡처(발송) 시점을 결정한다.
 *
 * | 함수 | 데이터 수집 | Sentry 캡처 | 폴백 타이머 |
 * | --- | --- | --- | --- |
 * | `registerApiError` | ✓ (axios 에러에서 추출) | ✗ | 예약 |
 * | `captureError` | ✓ | **✓** | 해제 |
 * | `prepareSdkCapture` | ✓ | SDK 가 대신 | 해제 |
 * | `promoteErrorMeta` | ✗ | ✗ | ✗ |
 * | `scheduleFallbackCapture` | ✗ | 나중에 | 예약 |
 *
 * 원래의 에러 처리 흐름을 막지 않기 위해, 모든 공개 함수는 에러를 전파하지 않음
 */

/** 태그 체계 버전. 스킴이 바뀌면 올려서 이전 데이터와 구분한다 */
export const META_VERSION = "1";

const FALLBACK_DELAY_MS = 2000;

/** Sentry 의 tags 값 타입(Primitive)과 같은 범위. @sentry/react 가 이 타입을 re-export 하지 않아 직접 맞춘다 */
type EventTagValue = number | string | boolean | bigint | symbol | null | undefined;

type EventTags = Record<string, EventTagValue>;
type EventContexts = Record<string, Record<string, unknown> | undefined>;

/** promoteErrorMeta 가 다루는 이벤트의 최소 형태 = Sentry 의 Event 스펙 기준 */
export type TaggableEvent = {
  tags?: EventTags;
  contexts?: EventContexts;
  fingerprint?: string[];
  exception?: { values?: Array<{ mechanism?: { type?: string } }> };
};

type ErrorDomain = "api" | "network" | "auth" | "render";

/** 에러 객체별로 예약된 폴백 타이머. 캡처되면 즉시 해제해 참조를 놓아준다 */
const pendingFallbacks = new WeakMap<object, ReturnType<typeof setTimeout>>();

const isFallbackEnabled = () => process.env.REACT_APP_SENTRY_FALLBACK_CAPTURE === "true";

const withoutUndefined = <V>(record: Record<string, V | undefined>): Record<string, V> => {
  const result: Record<string, V> = {};

  Object.entries(record).forEach(([key, value]) => {
    if (value !== undefined) result[key] = value;
  });

  return result;
};

const resolveDomain = (meta: ErrorMeta): ErrorDomain | undefined => {
  if (meta.render) return "render";
  if (meta.auth) return "auth";
  if (!meta.api) return undefined;
  // 응답이 없으면 네트워크 장애나 타임아웃
  return meta.api.status === undefined ? "network" : "api";
};

/**
 * 모든 API 이벤트가 에러 모달에서 캡처되어
 * 스택트레이스가 항상 동일하므로
 * 기본 그룹핑은 변별력이 없음
 * => 정의된 그루핑으로 커스터마이징
 */
const resolveFingerprint = (meta: ErrorMeta, domain: ErrorDomain | undefined): string[] | undefined => {
  if (domain === "api") {
    return ["api", meta.api?.path ?? "unknown", meta.api?.method ?? "unknown", String(meta.api?.status ?? 0), meta.api?.errorCode ?? ""];
  }
  // 네트워크 장애는 각 엔드포인트로 쪼개지 않고 하나로 수용
  if (domain === "network") return ["network", meta.api?.method ?? "unknown"];
  if (domain === "auth") return ["auth", meta.auth?.reason ?? "unknown"];
  if (domain === "render") return ["render", meta.render?.component ?? "unknown"];
  return undefined;
};

/** SDK 전역 핸들러(globalHandlers integration)가 붙이는 mechanism.type */
const GLOBAL_HANDLER_MECHANISMS = ["onunhandledrejection", "onerror"];

const isGlobalHandlerEvent = (event: TaggableEvent): boolean =>
  event.exception?.values?.some((value) => GLOBAL_HANDLER_MECHANISMS.includes(value.mechanism?.type ?? "")) ?? false;

/**
 * 표시된 모달은 SDK 전역 핸들러보다 항상 먼저 캡처하므로,
 * 전역 핸들러가 perceived 없이 가져온 모달 에러는 표시되지 않은 것
 * 코드에서 직접 캡처한 이벤트는 이 순서가 보장되지 않아 제외
 */
const resolvePerceived = (meta: ErrorMeta, event: TaggableEvent): boolean | undefined => {
  if (meta.perceived !== undefined) return meta.perceived;
  return meta.ui?.surface === "modal" && isGlobalHandlerEvent(event) ? false : undefined;
};

/**
 * 전파 경로가 끊겼는지 확인
 */
const resolveMetaComplete = (meta: ErrorMeta): boolean | undefined => {
  // perceived 가 없으면 우리 캡처 경로가 아니므로 undefined 반환
  if (meta.perceived === undefined) return undefined;
  // render가 있으면 true
  if (meta.render) return true;
  // api가 있으면 true
  return meta.api !== undefined;
};

/**
 *
 * `beforeSend`에서 호출
 * 에러 객체 안에 수집한 데이터를 태그,컨텍스트,fingerprint 로
 * 옮기며, 이벤트를 만들거나 보내지 않는다
 */
export const promoteErrorMeta = <T extends TaggableEvent>(event: T, originalException: unknown): T => {
  try {
    const meta = readErrorMetaChain(originalException);
    if (!meta) return event;

    const domain = resolveDomain(meta);
    const fingerprint = resolveFingerprint(meta, domain);
    const perceived = resolvePerceived(meta, event);
    const metaComplete = resolveMetaComplete({ ...meta, perceived });

    const tags = withoutUndefined<Exclude<EventTagValue, undefined>>({
      ...event.tags,
      "ctrl.meta_version": META_VERSION,
      "ctrl.err_domain": domain,
      "ctrl.user_perceived": perceived === undefined ? undefined : String(perceived),
      "ctrl.surface": meta.ui?.surface,
      "ctrl.meta_complete": metaComplete === undefined ? undefined : String(metaComplete),
      "ctrl.api_path": meta.api?.path,
      "ctrl.api_method": meta.api?.method,
      "ctrl.api_status": meta.api?.status,
      "ctrl.api_error_code": meta.api?.errorCode,
      "ctrl.api_track": meta.api?.track,
      "ctrl.page_route": typeof window === "undefined" ? undefined : matchPageRoute(window.location.pathname),
    });

    const contexts = withoutUndefined<Record<string, unknown>>({
      ...event.contexts,
      "ctrl.api": meta.api ? { ...meta.api } : undefined,
      "ctrl.ui": meta.ui ? { ...meta.ui } : undefined,
      "ctrl.extra": meta.extra,
    });

    return { ...event, tags, contexts, ...(fingerprint ? { fingerprint } : {}) };
  } catch {
    // 변환 실패로 이벤트 전송이 막히면 안됨
    return event;
  }
};

const cancelFallbackCapture = (error: unknown) => {
  if (!canAttachErrorMeta(error)) return;

  const timer = pendingFallbacks.get(error);
  if (timer === undefined) return;

  clearTimeout(timer);
  pendingFallbacks.delete(error);
};

/**
 * 에러 데이터를 바인드하고 Sentry로 캡처(전송)한다
 *
 * 이미 캡처된 에러는 아무것도 하지 않는다
 * 캡처한 뒤에는 예약돼 있던 폴백 타이머를 해제
 */
export const captureError = (error: unknown, meta: ErrorMeta): void => {
  try {
    if (hasBeenCaptured(error)) return;

    attachErrorMeta(error, meta);
    markCaptured(error);
    cancelFallbackCapture(error);
    Sentry.captureException(error);
  } catch {}
};

/**
 * SDK 가 캡처를 대신 수행하는 경로에서 사용
 * 메타만 붙이고 캡처는 호출하지 않는다
 *
 * Sentry.ErrorBoundary 는 beforeCapture -> 자체 captureException -> onError 순서로 동작하는데,
 * 여기서 직접 captureException 을 부르면 SDK 중복 방지에 걸려 SDK 쪽 캡처가 건너뛰어지고,
 * contexts.react.componentStack 과 componentStack 으로 만든 스택트레이스를 잃기 때문에
 * beforeCapture 시점에 메타만 붙인다
 */
export const prepareSdkCapture = (error: unknown, meta: ErrorMeta): void => {
  try {
    attachErrorMeta(error, meta);
    markCaptured(error);
    cancelFallbackCapture(error);
  } catch {}
};

/**
 * 사용자에게 UI 를 띄우지 않는 경로에서도 센트리로 에러를 수집하기 위한 함수
 * 일정 시간 뒤에도 아무도 캡처하지 않았으면 대신 캡처하도록 fallback
 */
export const scheduleFallbackCapture = (error: unknown): void => {
  try {
    if (!isFallbackEnabled()) return;
    if (!canAttachErrorMeta(error)) return;
    if (pendingFallbacks.has(error)) return;

    const timer = setTimeout(() => {
      pendingFallbacks.delete(error);
      if (hasBeenCaptured(error)) return;
      captureError(error, { perceived: false });
    }, FALLBACK_DELAY_MS);

    pendingFallbacks.set(error, timer);
  } catch {}
};

type AxiosLikeError = {
  config?: { url?: string; method?: string };
  response?: { status?: number; data?: { meta?: { errorCode?: string } } };
};

/**
 * API 에러를 수집 대상으로 등록
 * legacy와 react-query API 공통
 *
 * axios 에러에서 경로,메서드,상태코드,errorCode 를 뽑아 메타로 첨부하고,
 * 캡처 폴백을 예약함
 */
export const registerApiError = (error: unknown, track: ApiTrack): void => {
  try {
    const axiosLike = error as AxiosLikeError;
    const url = axiosLike?.config?.url;

    attachErrorMeta(error, {
      api: {
        url,
        path: matchApiPath(url),
        method: axiosLike?.config?.method?.toUpperCase(),
        status: axiosLike?.response?.status,
        errorCode: axiosLike?.response?.data?.meta?.errorCode,
        track,
      },
    });
    scheduleFallbackCapture(error);
  } catch {}
};
